import { useRef } from "react";
import cx from "classnames";
import { faTag } from "@fortawesome/pro-regular-svg-icons";

import { Optional } from "common/utils/types";
import { BasicCatalogImage, BasicCategorySummary } from "common/types";
import LinkButton from "common/components/LinkButton";
import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";

import TextChip from "client/common/components/content/TextChip";
import { useBreakpoints } from "client/common/hooks";
import { urls } from "client/routes";
import { sendSelectItem, useSendPostHogProductLineClick } from "client/analytics/utils";
import type { ProductLineListSource } from "client/analytics/types";

import { IProductLineResultFact, PersonalizationKind } from "../types";
import { getProductLineCardChip, ProductLineCardChip } from "../utils";

import styles from "../styles/ProductLineCard.module.less";

export type ProductLineForCard = {
  id: number;
  name: string;
  brand_name: string;
  prescription_required: boolean;
  categories: BasicCategorySummary[];
  primary_category_name: string;
  image: Optional<BasicCatalogImage>;
  description: string;
  facts: IProductLineResultFact[];
  is_featured: boolean;
  is_loyalty_program_featured?: boolean;
  patient_prescription_label: Optional<string>;
  personalization_kind?: Optional<PersonalizationKind>;
  promotion_label?: string;
};

type ProductLineCardProps = {
  productLine: ProductLineForCard;
  eventViewName: ProductLineListSource;
  eventCategoryId: number | null;
};

const chipVariantToBackgroundStyles: Record<ProductLineCardChip["variant"], string> = {
  info: styles.prescribed,
  "accent-fill": styles.recentlyPurchased,
  "grey-fill": styles.recentlyViewed,
  highlight: styles.featured,
};

const StandardProductLineCard = ({
  productLine,
  eventViewName,
  eventCategoryId,
}: ProductLineCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const sendPostHogProductLineClick = useSendPostHogProductLineClick();

  const { isPhoneAndSmaller } = useBreakpoints();
  const chip = getProductLineCardChip(productLine);
  const handleClick = () => {
    sendSelectItem(productLine, eventViewName);
    sendPostHogProductLineClick({
      source: eventViewName,
      id: productLine.id,
      category_id: eventCategoryId,
      personalization_kind: productLine.personalization_kind ?? null,
    });
  };

  return (
    <LinkButton
      className={styles.standardProductLineCard}
      key={productLine.name}
      dest={urls.products.detail({ params: { id: productLine.id } })}
      onClick={handleClick}
    >
      <div
        className={cx(styles.background, chip && chipVariantToBackgroundStyles[chip.variant])}
        ref={cardRef}
      >
        <div className={styles.content}>
          <div className={styles.imageContainer}>
            <CatalogImage
              image={productLine.image}
              size="medium"
              alt="Product"
              className={styles.image}
            />
            {productLine.promotion_label && (
              <div className={styles.promotion}>
                <Icon icon={faTag} /> {productLine.promotion_label}
              </div>
            )}
          </div>
          <div className={styles.names}>
            {chip && (
              <div className={styles.chip}>
                <TextChip variant={chip.variant} size={isPhoneAndSmaller ? "s" : "m"}>
                  {chip.label}
                </TextChip>
              </div>
            )}
            <div className={styles.name}>{productLine.name}</div>
            <div className={styles.description}>{productLine.description}</div>
          </div>
        </div>
      </div>
      {productLine.facts.length ? (
        <div className={styles.facts}>
          {productLine.facts?.map((fact) => (
            <div className={styles.fact} key={fact.label}>
              <div className={styles.factLabel}>{fact.label}</div>
              <div className={styles.factValue}>{fact.value}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.noFacts} />
      )}
    </LinkButton>
  );
};

const CompactProductLineCard = ({
  productLine,
  eventViewName,
  eventCategoryId,
}: ProductLineCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const sendPostHogProductLineClick = useSendPostHogProductLineClick();
  const chip = getProductLineCardChip(productLine);
  const handleClick = () => {
    sendSelectItem(productLine, eventViewName);
    sendPostHogProductLineClick({
      source: eventViewName,
      id: productLine.id,
      category_id: eventCategoryId,
      personalization_kind: productLine.personalization_kind ?? null,
    });
  };

  return (
    <LinkButton
      className={styles.compactProductLineCard}
      key={productLine.name}
      dest={urls.products.detail({ params: { id: productLine.id } })}
      onClick={handleClick}
    >
      <div
        className={cx(styles.background, chip && chipVariantToBackgroundStyles[chip.variant])}
        ref={cardRef}
      >
        <div className={styles.content}>
          <CatalogImage
            image={productLine.image}
            size="medium"
            alt="Product"
            className={styles.image}
          />
          <div className={styles.names}>
            {chip && (
              <div className={styles.chip}>
                <TextChip variant={chip.variant} size="s">
                  {chip.label}
                </TextChip>
              </div>
            )}
            <div className={styles.name}>{productLine.name}</div>
            <div className={styles.description}>{productLine.description}</div>
            {productLine.promotion_label && (
              <div className={styles.promotion}>
                <Icon icon={faTag} /> {productLine.promotion_label}
              </div>
            )}
          </div>
        </div>
      </div>
    </LinkButton>
  );
};

export default Object.assign(StandardProductLineCard, { Compact: CompactProductLineCard });
